import type { ItineraryItem } from '@elara/client-core/models/ItineraryItemModel';

import React from 'react';

import { SUPPORT_EMAIL } from '@elara/client-core/lib/support-chat';

import { useItineraryState } from '#states/hooks.js';
import { Alert, AlertDescription, AlertTitle, Button, IconSizes, MaterialIcon } from '#ui/index.js';

/**
 * Booking-lifecycle controls for a room item, shared by the bookings view and the itinerary builder.
 * For an automated (Sabre or Travelport) hotel booking still inside its free-cancellation window it offers a
 * self-service cancel, and once cancelling would forfeit the payment it points at the team instead; for a booking made outside
 * Elara it points the traveler at the property; for an Elara-managed manual booking it shows a
 * "contact us to change/cancel" notice. Renders nothing for a non-room item, a cancelled item, or a
 * still-planned room (which has nothing to cancel yet).
 */
export function BookingCancelControls({ item }: { item: ItineraryItem }): React.ReactNode {
  if (item.type !== 'room' || item.isCancelled) return null;

  if (item.canSelfCancelBooking) {
    return <RoomCancelSection item={item} />;
  }

  // Cancelling now would forfeit what was paid — route it through the team, who cancel with the hotel
  // directly, instead of offering a one-tap button.
  if (item.cancellingForfeitsPayment) {
    return (
      <Alert variant="warning" className="flex gap-3">
        <MaterialIcon icon="info" size={IconSizes.sm} className="shrink-0" />
        <div>
          <AlertTitle>Need to cancel this booking?</AlertTitle>
          <AlertDescription>
            {item.cancellationDeadlineLabel && (
              <span className="mb-1 block font-medium">{item.cancellationDeadlineLabel}</span>
            )}
            You&rsquo;d lose what you&rsquo;ve paid so far if you cancel. Contact us{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">
              by email
            </a>{' '}
            or in-app chat and we&rsquo;ll contact the hotel directly to cancel.
          </AlertDescription>
        </div>
      </Alert>
    );
  }

  // Manually-recorded (non-GDS) bookings can't be cancelled in-app — point the traveler somewhere.
  if (item.isBooked || item.isApproved) {
    const booking = item.booking;
    // Booked outside Elara — we can't change or cancel it, so point to the property (and the provider
    // it was booked through, when known) rather than our own support.
    const isExternal = booking?.managedByElara === false || booking?.channel === 'manual';
    return (
      <Alert variant="info" className="flex gap-3">
        <MaterialIcon icon="info" size={IconSizes.sm} className="shrink-0" />
        <div>
          <AlertTitle>Need to change or cancel this booking?</AlertTitle>
          {isExternal ? (
            <AlertDescription>
              This booking was made outside of Elara, so please contact the hotel directly to change or cancel it.
            </AlertDescription>
          ) : (
            <AlertDescription>
              Please contact us{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">
                by email
              </a>{' '}
              or in-app chat.
            </AlertDescription>
          )}
        </div>
      </Alert>
    );
  }

  return null;
}

/**
 * Self-service cancel for an automated (Sabre or Travelport) hotel booking. A two-step confirm surfaces the frozen
 * cancellation policy before calling CancelHotelBooking, which cancels + verifies the reservation at the GDS that made it.
 */
function RoomCancelSection({ item }: { item: ItineraryItem }): React.ReactNode {
  const itineraryState = useItineraryState();
  const [confirming, setConfirming] = React.useState(false);
  const [pending, setPending] = React.useState(false);
  // Centralized on the item model so this prompt can't drift from what `ViewBookingDialog`/the room
  // guide show for this exact booking.
  const policy = item.cancellationPolicyText;
  const deadlineLabel = item.cancellationDeadlineLabel;

  const onConfirm = async () => {
    setPending(true);
    try {
      await itineraryState.cancelHotelBooking(item.id);
    } catch {
      // The state action already surfaced a toast.
    } finally {
      setPending(false);
      setConfirming(false);
    }
  };

  if (!confirming) {
    return (
      <Button variant="destructive" className="w-full" onClick={() => setConfirming(true)}>
        Cancel booking
      </Button>
    );
  }

  return (
    <Alert variant="warning" className="flex flex-col gap-3">
      <AlertTitle>Cancel this reservation?</AlertTitle>
      <AlertDescription>
        {deadlineLabel && <span className="mb-1 block font-medium">{deadlineLabel}</span>}
        {policy && <span className="mb-2 block">{policy}</span>}
        This cancels the reservation with the property and can&rsquo;t be undone.
      </AlertDescription>
      <div className="flex gap-2">
        <Button variant="destructive" size="sm" disabled={pending} onClick={onConfirm}>
          {pending ? 'Cancelling…' : 'Yes, cancel booking'}
        </Button>
        <Button variant="outline" size="sm" disabled={pending} onClick={() => setConfirming(false)}>
          Keep booking
        </Button>
      </div>
    </Alert>
  );
}
